import React, { useState, useRef, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Plus, MoreHorizontal, List, X, Search, Filter } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ListItem {
  id: string;
  text: string;
  completed: boolean;
}

interface ListData {
  id: string;
  name: string;
  color: string;
  items: ListItem[];
}

export function Lists() {
  const [lists, setLists] = useState<ListData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [newItemText, setNewItemText] = useState('');
  const [activeListId, setActiveListId] = useState<string | null>(null);
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [editingListName, setEditingListName] = useState('');
  const [editingListColor, setEditingListColor] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Load lists from Supabase on mount
  useEffect(() => {
    let subscription: any;

    const fetchLists = async () => {
      try {
        setLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        // Set up realtime subscription
        subscription = supabase
          .channel('lists_changes')
          .on('postgres_changes', 
            {
              event: '*',
              schema: 'public',
              table: 'lists',
              filter: `user_id=eq.${user.id}`
            },
            (payload) => {
              console.log('Received realtime update:', payload);
              fetchLists();
            }
          )
          .subscribe();

        const { data, error } = await supabase
          .from('lists')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        setLists(data?.map(list => ({
          id: list.id,
          name: list.name,
          color: list.color,
          items: list.items || []
        })) || []);
      } catch (err) {
        console.error('Error fetching lists:', err);
        setError('Failed to load lists');
      } finally {
        setLoading(false);
      }
    };

    fetchLists();

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  // Save list changes to Supabase
  const saveListToSupabase = async (list: ListData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      console.log('Saving list to Supabase:', list);

      const { error } = await supabase
        .from('lists')
        .upsert({
          id: list.id,
          user_id: user.id,
          name: list.name,
          color: list.color,
          items: list.items
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error saving list:', error);
      setError('Failed to save list');
    }
  };

  const handleStartEditingList = (list: ListData) => {
    setEditingListId(list.id);
    setEditingListName(list.name);
    setEditingListColor(list.color);
  };

  const handleSaveListName = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingListName.trim()) return;
    
    const updatedList = lists.find(list => list.id === editingListId);
    if (updatedList) {
      const newList = {
        ...updatedList,
        name: editingListName.trim(),
        color: editingListColor
      };
      setLists(lists.map(list => 
        list.id === editingListId ? newList : list
      ));
      saveListToSupabase(newList);
    }
    setEditingListId(null);
  };

  const handleAddItem = (listId: string, e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemText.trim()) return;

    const updatedList = lists.find(list => list.id === listId);
    if (updatedList) {
      const newList = {
        ...updatedList,
        items: [
          ...updatedList.items,
          {
            id: crypto.randomUUID(),
            text: newItemText.trim(),
            completed: false
          }
        ]
      };
      setLists(lists.map(list => 
        list.id === listId ? newList : list
      ));
      saveListToSupabase(newList);
    }

    setNewItemText('');
    setActiveListId(null);
  };

  const handleToggleItem = (listId: string, itemId: string) => {
    const updatedList = lists.find(list => list.id === listId);
    if (updatedList) {
      const newList = {
        ...updatedList,
        items: updatedList.items.map(item =>
          item.id === itemId ? { ...item, completed: !item.completed } : item
        )
      };
      setLists(lists.map(list => 
        list.id === listId ? newList : list
      ));
      saveListToSupabase(newList);
    }
  };

  const handleDeleteItem = (listId: string, itemId: string) => {
    const updatedList = lists.find(list => list.id === listId);
    if (updatedList) {
      const newList = {
        ...updatedList,
        items: updatedList.items.filter(item => item.id !== itemId)
      };
      setLists(lists.map(list => 
        list.id === listId ? newList : list
      ));
      saveListToSupabase(newList);
    }
  };

  const handleAddList = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const listId = crypto.randomUUID();
    const newList: ListData = {
      id: listId,
      name: 'New List',
      color: '#C084FC',
      items: []
    };

    try {
      // Save to Supabase first
      const { error } = await supabase
        .from('lists')
        .insert({
          id: listId,
          user_id: user.id,
          name: newList.name,
          color: newList.color,
          items: newList.items
        });

      if (error) throw error;

      // Update local state after successful save
      setLists(prev => [newList, ...prev]);
    
      // Scroll to new list after it's added
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
      });
    } catch (err) {
      console.error('Error creating list:', err);
      setError('Failed to create list');
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!scrollRef.current) return;
    setStartX(e.touches[0].pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {error && (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-xl">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-[var(--accent-color)] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold">Lists</h1>
              </div>
            </div>

            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search lists..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-black/20 rounded-xl text-white placeholder-gray-400"
              />
            </div>

            {/* Lists Container */}
            <div 
              ref={scrollRef}
              className="flex gap-4 overflow-x-auto pb-28 md:pb-20 hide-scrollbar"
              style={{ touchAction: 'pan-y' }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {lists
                .filter(list => 
                  list.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  list.items.some(item => 
                    item.text.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                )
                .map((list) => (
                <div
                  key={list.id}
                  className="flex-shrink-0 w-[320px] min-h-[calc(100vh-16rem)] rounded-[32px] transition-transform"
                  style={{ backgroundColor: list.color }}
                >
                  <div className="p-6 h-full flex flex-col">
                    {editingListId === list.id ? (
                      <form onSubmit={handleSaveListName} className="mb-4">
                        <div className="flex gap-2">
                          <div className="flex-1 space-y-2">
                            <input
                              type="text"
                              value={editingListName}
                              onChange={(e) => setEditingListName(e.target.value)}
                              className="w-full px-3 py-2 text-2xl font-bold bg-transparent text-white border-b-2 border-white/20 focus:border-white focus:outline-none"
                              autoFocus
                            />
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={editingListColor}
                                onChange={(e) => setEditingListColor(e.target.value)}
                                className="w-8 h-8 rounded-lg cursor-pointer border-2 border-white/20 hover:border-white/40 transition-colors"
                              />
                              <button
                                type="submit"
                                className="px-3 py-1 bg-black/20 rounded-lg text-sm hover:bg-black/30 transition-colors"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    ) : (
                      <div className="flex justify-between items-center mb-4">
                        <div 
                          className="text-2xl font-bold text-white cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => handleStartEditingList(list)}
                        >
                          {list.name}
                        </div>
                        <button
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this list?')) {
                              setLists(lists.filter(l => l.id !== list.id));
                              supabase
                                .from('lists')
                                .delete()
                                .eq('id', list.id)
                                .then(({ error }) => {
                                  if (error) {
                                    console.error('Error deleting list:', error);
                                    setError('Failed to delete list');
                                  }
                                });
                            }
                          }}
                          className="p-2 hover:bg-black/20 rounded-lg transition-colors text-white/80"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    {/* Add Item Form */}
                    {activeListId === list.id ? (
                      <form 
                        onSubmit={(e) => handleAddItem(list.id, e)}
                        className="mb-4"
                      >
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newItemText}
                            onChange={(e) => setNewItemText(e.target.value)}
                            placeholder="Add new item..."
                            className="flex-1 px-3 py-2 rounded-xl bg-black/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/20"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => setActiveListId(null)}
                            className="p-2 hover:bg-black/20 rounded-xl transition-colors"
                          >
                            <X className="w-5 h-5 text-white" />
                          </button>
                        </div>
                      </form>
                    ) : (
                      <button
                        onClick={() => setActiveListId(list.id)}
                        className="mb-4 flex items-center gap-2 px-3 py-2 rounded-xl bg-black/20 text-white/60 hover:bg-black/30 transition-colors w-full"
                      >
                        <Plus className="w-5 h-5" />
                        <span>Add Item</span>
                      </button>
                    )}

                    {/* Items List */}
                    {list.items.length === 0 ? (
                      <div className="flex-1 flex flex-col items-center justify-center text-white/60">
                        <List className="w-8 h-8 mb-2" />
                        <p>No items yet</p>
                      </div>
                    ) : (
                      <div className="flex-1 space-y-2">
                        {list.items.map(item => (
                          <div 
                            key={item.id} 
                            className="flex items-center gap-2 p-3 bg-black/10 rounded-xl group"
                          >
                            <input
                              type="checkbox"
                              checked={item.completed}
                              onChange={() => handleToggleItem(list.id, item.id)}
                              className="rounded-full border-2 border-white/20 bg-transparent checked:bg-white checked:border-white focus:ring-0 focus:ring-offset-0"
                            />
                            <span className={`flex-1 ${item.completed ? 'line-through text-white/60' : 'text-white'}`}>
                              {item.text}
                            </span>
                            <button
                              onClick={() => handleDeleteItem(list.id, item.id)}
                              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-black/20 rounded-lg transition-all"
                            >
                              <X className="w-4 h-4 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Add List Button */}
        <button
          onClick={handleAddList}
          className="fixed bottom-24 right-6 md:bottom-8 w-14 h-14 bg-[var(--accent-color)] rounded-full shadow-lg flex items-center justify-center hover:opacity-90 transition-opacity z-[9999]"
          style={{ touchAction: 'manipulation' }}
        >
          <Plus className="w-6 h-6 text-black" />
        </button>
      </div>
    </Layout>
  );
}